import { deploy, personalityIntent, weakestNeighbor } from "./bot-personalities.js";
import {
  type BotDecision,
  type BotPlanningContext,
  type Selected,
  selected,
} from "./bot-policy-contract.js";
import { botReinforcement } from "./bot-reinforcement.js";
import type { BotDifficulty, BotMemory } from "./bot-types.js";
import { BOT_DIFFICULTIES } from "./content/setup.js";
import { microFromString } from "./exact-numbers.js";
import type { SeatObservation } from "./types.js";

export function initialBotMemory(): BotMemory {
  return Object.freeze({
    decision: 0,
    seedCursor: 0,
    lastTargetId: null,
    lastAction: null,
    diplomacyCooldownUntilMs: 0,
  });
}

export function botIntervalMs(difficulty: BotDifficulty): number {
  const found = BOT_DIFFICULTIES.find((entry) => entry.id === difficulty);
  if (!found) throw new TypeError(`Unknown bot difficulty: ${difficulty}`);
  return found.decisionSeconds * 1_000;
}

export function botProjectionMs(difficulty: BotDifficulty): number {
  const found = BOT_DIFFICULTIES.find((entry) => entry.id === difficulty);
  if (!found) throw new TypeError(`Unknown bot difficulty: ${difficulty}`);
  return found.projectionSeconds * 1_000;
}

export function decideBot(
  observation: SeatObservation,
  memory: BotMemory,
  context: BotPlanningContext,
): BotDecision {
  validateBotMemory(memory);
  const projected =
    memory.decision % 3 === 2 ? planningResponse(observation, context.projected) : undefined;
  const chosen =
    (memory.decision % 3 === 1 ? botReinforcement(observation) : undefined) ??
    projected ??
    personalityIntent(observation, memory, context) ??
    commonIntent(observation, memory, context);
  const intent = chosen?.intent ?? ({ kind: "wait" } as const);
  return Object.freeze({
    intent,
    reason: chosen?.reason ?? "No legal useful action",
    projectionHorizonMs: botProjectionMs(context.difficulty),
    memory: Object.freeze({
      decision: memory.decision + 1,
      seedCursor: memory.seedCursor + 1,
      lastTargetId: chosen?.targetId ?? memory.lastTargetId,
      lastAction: intent.kind,
      diplomacyCooldownUntilMs:
        intent.kind === "propose-pact" || intent.kind === "send-metal"
          ? observation.gameTimeMs + 60_000
          : memory.diplomacyCooldownUntilMs,
    }),
  });
}

function planningResponse(
  current: SeatObservation,
  projected?: SeatObservation,
): Selected | undefined {
  if (!projected) return undefined;
  if (
    microFromString(projected.self.headquartersIntegrity) <
    microFromString(current.self.headquartersIntegrity)
  )
    return selected(
      {
        kind: "set-power",
        industry: 60,
        defenses: 100,
        militaryProduction: 90,
        research: 40,
      },
      "Projection predicts Headquarters damage; reinforce defense",
    );
  if (microFromString(projected.self.metal) < microFromString(current.self.metal))
    return selected(
      {
        kind: "set-metal-priority",
        priority: ["construction", "producer-factories", "troop-assembly"],
      },
      "Projection predicts a Metal shortage; preserve reinvestment priority",
    );
  return undefined;
}

function commonIntent(
  observation: SeatObservation,
  memory: BotMemory,
  context: BotPlanningContext,
): Selected | undefined {
  const target = weakestNeighbor(observation, context, memory);
  if (!target) return undefined;
  if (memory.decision % 2 === 0)
    return selected(
      { kind: "set-standing-order", borderId: target.id, order: "assault" },
      "Advance on the weakest neighboring town",
      target.id,
    );
  return deploy(observation, target.id);
}

export function validateBotMemory(value: BotMemory): void {
  if (
    !value ||
    !Number.isSafeInteger(value.decision) ||
    value.decision < 0 ||
    !Number.isSafeInteger(value.seedCursor) ||
    value.seedCursor < 0 ||
    (value.lastTargetId !== null && typeof value.lastTargetId !== "string") ||
    (value.lastAction !== null && typeof value.lastAction !== "string") ||
    !Number.isSafeInteger(value.diplomacyCooldownUntilMs) ||
    value.diplomacyCooldownUntilMs < 0
  )
    throw new TypeError("Invalid bot memory");
}
